import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Calendar, MapPin, Receipt, Star, Plus, X, Loader2, Crown } from "lucide-react";
import ChangePasswordCard from "../components/ChangePasswordCard";
import NotificationPreferencesCard from "../components/NotificationPreferencesCard";

const STATUS_STYLES = {
  pending: "bg-[#D4AF37]/15 text-[#A88926] border-[#D4AF37]/30",
  confirmed: "bg-[#0B5D3B]/10 text-[#0B5D3B] border-[#0B5D3B]/20",
  in_progress: "bg-blue-50 text-blue-700 border-blue-100",
  completed: "bg-green-50 text-green-700 border-green-100",
  cancelled: "bg-red-50 text-red-700 border-red-100",
};

export default function ClientDashboard() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewFor, setReviewFor] = useState(null);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: "" });
  const [reviewUrl, setReviewUrl] = useState("https://www.google.com/search?q=ShoNics+Kleaning+Solutions+Atlanta+reviews");
  const [membership, setMembership] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bookings/mine");
      setBookings(data);
    } catch { toast.error("Could not load bookings"); }
    setLoading(false);
  };

  useEffect(() => {
    load();
    api.get("/settings").then(r => setReviewUrl(r.data.google_review_url)).catch(() => {});
    api.get("/membership/me").then(r => setMembership(r.data.plan)).catch(() => {});
  }, []);

  const cancel = async (id) => {
    try {
      await api.delete(`/bookings/${id}`);
      toast.success("Booking cancelled");
      load();
    } catch { toast.error("Could not cancel"); }
  };

  const submitReview = async () => {
    try {
      await api.post("/reviews", { booking_id: reviewFor.id, rating: reviewForm.rating, comment: reviewForm.comment });
      toast.success("Thanks for your review!");
      setReviewFor(null);
      setReviewForm({ rating: 5, comment: "" });
    } catch { toast.error("Could not submit review"); }
  };

  const upcoming = bookings.filter(b => ["pending","confirmed","in_progress"].includes(b.status));
  const past = bookings.filter(b => ["completed","cancelled"].includes(b.status));
  const totalSpent = bookings.filter(b => b.status === "completed").reduce((a,b) => a + (b.estimated_price||0), 0);

  return (
    <div className="container-x py-16 md:py-24" data-testid="client-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
        <div>
          <div className="overline mb-2">My Account</div>
          <h1 className="font-serif text-4xl md:text-5xl text-[#111]">Welcome, {user?.name?.split(" ")[0]}.</h1>
        </div>
        <Link to="/booking" className="btn-primary" data-testid="new-booking-btn"><Plus className="w-4 h-4 mr-2" /> New booking</Link>
      </div>

      {/* Membership card */}
      {membership && (
        <div className={`mb-10 rounded-2xl p-7 md:p-8 relative overflow-hidden ${
          membership.id === "free"
            ? "bg-white border border-[#EAE3D2]"
            : "bg-gradient-to-br from-[#063A24] via-[#0B5D3B] to-[#063A24] text-white shadow-[0_24px_60px_rgba(11,93,59,0.18)]"
        }`} data-testid="membership-card">
          <div className="flex items-center gap-3 mb-3">
            <Crown className={`w-5 h-5 ${membership.id === "free" ? "text-[#7A8A81]" : "text-[#D4AF37]"}`} strokeWidth={1.6} />
            <div className={`text-[10px] uppercase tracking-[0.28em] font-semibold ${membership.id === "free" ? "text-[#7A8A81]" : "text-[#D4AF37]"}`}>
              {membership.id === "free" ? "No active membership" : "Active membership"}
            </div>
          </div>
          <h3 className={`font-serif text-3xl ${membership.id === "free" ? "text-[#111]" : "text-white"}`}>{membership.name}</h3>
          <p className={`text-sm mt-1 ${membership.id === "free" ? "text-[#7A8A81]" : "text-white/80"}`}>
            {membership.id === "free"
              ? "Join Sparkle or Crystal to save 10–15% on every visit and lock in priority booking."
              : `${membership.discount_percent}% off every clean is automatically applied at booking.`}
          </p>
          <div className="mt-5">
            <Link
              to="/membership"
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold ${
                membership.id === "free"
                  ? "bg-[#0B5D3B] text-white hover:bg-[#063A24]"
                  : "bg-[#D4AF37] text-[#0B5D3B] hover:bg-white"
              } transition-colors`}
              data-testid="membership-cta"
            >
              {membership.id === "free" ? "Compare plans" : "Manage membership"}
            </Link>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
        {[
          { label: "Upcoming", value: upcoming.length, icon: Calendar },
          { label: "Completed", value: past.filter(b=>b.status==="completed").length, icon: Star },
          { label: "Total spent", value: `$${totalSpent}`, icon: Receipt },
          { label: "Service area", value: bookings[0]?.city || "—", icon: MapPin },
        ].map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} className="card-luxe p-6" data-testid={`stat-card-${i}`}>
              <Icon className="w-5 h-5 text-[#D4AF37] mb-3" />
              <div className="font-serif text-3xl text-[#0B5D3B]">{s.value}</div>
              <div className="text-xs uppercase tracking-[0.18em] text-[#7A8A81] mt-1">{s.label}</div>
            </div>
          );
        })}
      </div>

      {/* Upcoming bookings */}
      <h2 className="font-serif text-2xl mb-5">Upcoming</h2>
      {loading ? <Loader2 className="animate-spin text-[#0B5D3B]" /> : (
        upcoming.length === 0 ? <div className="text-[#7A8A81] mb-12">No upcoming bookings — <Link to="/booking" className="text-[#0B5D3B] font-medium">book one now</Link>.</div> : (
          <div className="space-y-3 mb-14" data-testid="upcoming-bookings">
            {upcoming.map((b) => (
              <div key={b.id} className="card-luxe p-6 grid md:grid-cols-5 gap-4 items-center" data-testid={`booking-${b.id}`}>
                <div className="md:col-span-2">
                  <div className="font-serif text-xl text-[#111]">{b.service_name}</div>
                  <div className="text-xs text-[#7A8A81] mt-1">{b.address}, {b.city}</div>
                </div>
                <div className="text-sm">
                  <div className="text-[#7A8A81]">Date</div>
                  <div className="font-medium">{b.date} · {b.time_slot}</div>
                </div>
                <div className="text-sm">
                  <div className="text-[#7A8A81]">Total</div>
                  <div className="font-medium">${b.estimated_price}</div>
                </div>
                <div className="flex items-center gap-2 justify-end">
                  <span className={`text-[10px] uppercase tracking-widest px-3 py-1 rounded-full border ${STATUS_STYLES[b.status]}`}>{b.status.replace("_"," ")}</span>
                  <button onClick={() => cancel(b.id)} className="text-xs text-[#7A8A81] hover:text-red-600" data-testid={`cancel-${b.id}`}>Cancel</button>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Google Review card */}
      <div className="card-luxe p-7 md:p-9 mb-14 grid md:grid-cols-3 gap-6 items-center bg-gradient-to-br from-white via-[#FAF7EE] to-white" data-testid="google-review-card">
        <div className="md:col-span-2">
          <div className="overline mb-3">Loved your clean?</div>
          <h3 className="font-serif text-2xl md:text-3xl text-[#111] leading-tight">Drop us a 5-star Google review.</h3>
          <p className="mt-3 text-[#4A554E] text-sm md:text-base">It takes 30 seconds and helps neighbors discover ShoNics. As a thank-you, every verified review unlocks a $10 credit on your next service.</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <a
              href={reviewUrl}
              target="_blank"
              rel="noreferrer"
              className="btn-primary text-sm py-2.5 px-5"
              data-testid="review-google-btn"
            >
              <Star className="w-4 h-4 mr-2 fill-[#D4AF37] text-[#D4AF37]" /> Leave a Google Review
            </a>
            <button
              onClick={() => {
                navigator.clipboard.writeText(reviewUrl);
                toast.success("Review link copied!");
              }}
              className="text-sm text-[#0B5D3B] font-medium px-5 py-2.5 rounded-full border border-[#EAE3D2] hover:border-[#0B5D3B]"
              data-testid="copy-review-link"
            >
              Copy link
            </button>
          </div>
        </div>
        <div className="flex md:justify-end">
          <div className="bg-white rounded-2xl border border-[#EAE3D2] p-3 shadow-[0_8px_24px_rgba(11,93,59,0.06)]">
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(reviewUrl)}&color=0B5D3B&bgcolor=FFFFFF`}
              alt="Scan to review"
              className="w-44 h-44"
              data-testid="review-qr"
            />
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#7A8A81] text-center mt-2">Scan with phone</div>
          </div>
        </div>
      </div>

      {/* Past bookings */}
      <h2 className="font-serif text-2xl mb-5">History</h2>
      {past.length === 0 ? <div className="text-[#7A8A81]">No past bookings yet.</div> : (
        <div className="space-y-3" data-testid="past-bookings">
          {past.map((b) => (
            <div key={b.id} className="card-luxe p-6 grid md:grid-cols-5 gap-4 items-center">
              <div className="md:col-span-2">
                <div className="font-serif text-xl text-[#111]">{b.service_name}</div>
                <div className="text-xs text-[#7A8A81] mt-1">{b.address}, {b.city}</div>
              </div>
              <div className="text-sm">
                <div className="text-[#7A8A81]">Date</div>
                <div>{b.date}</div>
              </div>
              <div className="text-sm">
                <div className="text-[#7A8A81]">Total</div>
                <div>${b.estimated_price}</div>
              </div>
              <div className="flex items-center gap-2 justify-end">
                <span className={`text-[10px] uppercase tracking-widest px-3 py-1 rounded-full border ${STATUS_STYLES[b.status]}`}>{b.status.replace("_"," ")}</span>
                {b.status === "completed" && (
                  <button onClick={() => setReviewFor(b)} className="text-xs text-[#0B5D3B] font-medium" data-testid={`review-${b.id}`}>Review</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-10">
        <ChangePasswordCard />
      </div>

      <div className="mt-6">
        <NotificationPreferencesCard />
      </div>

      {/* Review dialog */}
      {reviewFor && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="review-modal">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <div className="flex justify-between mb-4">
              <h3 className="font-serif text-2xl">Leave a review</h3>
              <button onClick={() => setReviewFor(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="text-xs text-[#7A8A81] uppercase tracking-widest mb-1">Rating</div>
            <div className="flex gap-1 mb-4">
              {[1,2,3,4,5].map((n) => (
                <button key={n} onClick={() => setReviewForm({ ...reviewForm, rating: n })} data-testid={`rating-${n}`}>
                  <Star className={`w-7 h-7 ${n <= reviewForm.rating ? "fill-[#D4AF37] text-[#D4AF37]" : "text-[#EAE3D2]"}`} />
                </button>
              ))}
            </div>
            <textarea value={reviewForm.comment} onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
              rows={4} placeholder="Share your experience..."
              className="w-full px-4 py-3 rounded-2xl border border-[#EAE3D2] focus:border-[#0B5D3B] focus:outline-none text-sm"
              data-testid="review-comment" />
            <button onClick={submitReview} className="btn-primary w-full mt-4" data-testid="submit-review">Submit</button>
          </div>
        </div>
      )}
    </div>
  );
}
